import { useState, useEffect } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "redux/store/index";
import { useGetCancersQuery } from "redux/services/cancerApi";
import { usePostRoleSelectionMutation } from "redux/services/usersApi";
import {
  customStyles,
  DropdownIndicator,
} from "styles/reactSelectCustomStyles";
import {
  convertCancersToGroupedOptions,
  findInGroupedOptions,
} from "Helpers/convertCancersToGroupedOptions";
import styles from "./RoleSelectionForm.module.scss";
import { ReactComponent as Logo } from "assets/Icons/Alivia_logo.svg";

interface RoleSelectionFormData {
  rolePatient: boolean;
  roleGuardian: boolean;
  cancerId: string;
  agreeMedicalInfo: boolean;
}

const RoleSelectionForm = () => {
  const { authUser } = useSelector((state: RootState) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data: cancersData, isLoading: isCancersLoading } = useGetCancersQuery(
    {
      offset: "0",
      limit: "0",
    }
  );

  const [postRoleSelection] = usePostRoleSelectionMutation();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RoleSelectionFormData>({
    defaultValues: {
      rolePatient: false,
      roleGuardian: false,
      cancerId: "",
      agreeMedicalInfo: false,
    },
  });

  const watchRolePatient = watch("rolePatient");
  const watchRoleGuardian = watch("roleGuardian");

  // Clear cancer selection when patient role is unchecked
  useEffect(() => {
    if (!watchRolePatient) {
      setValue("cancerId", "");
      setValue("agreeMedicalInfo", false);
    }
  }, [watchRolePatient, setValue]);

  const cancerOptions = convertCancersToGroupedOptions(
    cancersData?.data || []
  );

  const submitHandler: SubmitHandler<RoleSelectionFormData> = async (data) => {
    setIsSubmitted(true);

    // Walidacja
    if (!data.rolePatient && !data.roleGuardian) {
      return;
    }
    if (data.rolePatient && !data.cancerId) {
      return;
    }
    if (data.rolePatient && !data.agreeMedicalInfo) {
      return;
    }

    setIsSubmitting(true);

    try {
      const roleCodes: string[] = [];
      if (data.rolePatient) roleCodes.push("patient");
      if (data.roleGuardian) roleCodes.push("guardian");

      // Jedno żądanie: onko-api zapisuje role, nowotwór i zgodę,
      // a następnie sam aktualizuje Salesforce ze stanu bazy.
      await postRoleSelection({
        userId: authUser.id,
        data: {
          roleCodes,
          cancerId: data.cancerId ? parseInt(data.cancerId, 10) : undefined,
          agreeMedicalInfo: data.rolePatient ? data.agreeMedicalInfo : undefined,
        },
      }).unwrap();

      // Spinner zostaje — przekierowanie nastąpi po refetchu useGetMeQuery
      // (invalidacja tagu "Me").
    } catch (error) {
      toast.error("Wystąpił błąd podczas przypisywania roli");
      setIsSubmitting(false);
    }
  };

  if (isCancersLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.leftPanel}>
          <div className={styles.leftPanelContent}>
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
              <Spinner animation="border" variant="primary" />
            </div>
          </div>
        </div>
        <div className={styles.rightPanel} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Left Panel - Form */}
      <div className={styles.leftPanel}>
        <div className={styles.leftPanelContent}>
          {/* Logo */}
          <div className="mb-5">
            <Logo style={{ width: 153 }} />
          </div>

          {/* Form */}
          <Form onSubmit={handleSubmit(submitHandler)} className="d-grid gap-4">
            <div>
              <Form.Label className="form-label fw-bold">
                Jestem tutaj, ponieważ:
                <span className="text-primary ms-1">*</span>
              </Form.Label>

              <div>
                <Controller
                  name="rolePatient"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Check
                      type="checkbox"
                      id="rolePatient"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      label="choruję na nowotwór (lub jestem w trakcie diagnozy)"
                    />
                  )}
                />

                <Controller
                  name="roleGuardian"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Check
                      type="checkbox"
                      id="roleGuardian"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      label="wspieram bliską osobę"
                    />
                  )}
                />
              </div>

              {isSubmitted && !watchRolePatient && !watchRoleGuardian && (
                <div className="invalid-feedback d-block">
                  Wybierz przynajmniej jedną opcję
                </div>
              )}

              {watchRolePatient && (
                <div className="mt-4">
                  <Form.Label className="form-label fw-bold">
                    O jakim typie nowotworu szukasz informacji?
                    <span className="text-primary ms-1">*</span>
                  </Form.Label>

                  <Controller
                    name="cancerId"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (watchRolePatient && !value) {
                          return "Wybierz typ nowotworu";
                        }
                        return true;
                      },
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                      <Select
                        ref={ref}
                        value={findInGroupedOptions(cancerOptions, value)}
                        classNamePrefix="select"
                        placeholder="Wpisz lub wyszukaj"
                        isClearable={true}
                        isSearchable={true}
                        options={cancerOptions}
                        onChange={(option: SingleValue<{ value: string; label: string }>) =>
                          onChange(option?.value || "")
                        }
                        styles={customStyles(!!errors?.cancerId?.message)}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => "Brak wyników"}
                      />
                    )}
                  />

                  {isSubmitted && errors.cancerId && (
                    <div className="invalid-feedback d-block">
                      {errors.cancerId.message}
                    </div>
                  )}

                  <div className="mt-4">
                    <Controller
                      name="agreeMedicalInfo"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (watchRolePatient && !value) {
                            return "Zaznaczenie zgody jest wymagane";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Form.Check
                          type="checkbox"
                          id="agreeMedicalInfo"
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                          label="Potwierdzam, że choruję na nowotwór złośliwy lub jestem w trakcie diagnostyki onkologicznej. Wyrażam zgodę na przetwarzanie danych dotyczących mojego zdrowia."
                        />
                      )}
                    />

                    {isSubmitted && errors.agreeMedicalInfo && (
                      <div className="invalid-feedback d-block">
                        {errors.agreeMedicalInfo.message}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-100 text-white"
            >
              {isSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Kontynuuj"
              )}
            </Button>
          </Form>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className={styles.rightPanel} />
    </div>
  );
};

export default RoleSelectionForm;
